import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { useAuth } from '../../lib/auth';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  LayoutDashboard,
  Users,
  FileText,
  ClipboardList,
  Settings,
  Shield,
  LogOut,
  PanelLeftClose,
  PanelLeft,
  Receipt,
  Sparkles,
  Calendar,
  FileDown,
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_caredata-suite/artifacts/8itk90ub_55315630-dcf1-43bd-9c0d-4f1818f5e217.jpeg";

const navItems = [
  { 
    title: 'Dashboard', 
    icon: LayoutDashboard, 
    href: '/emr/dashboard',
    permission: null 
  },
  { 
    title: 'Scheduling', 
    icon: Calendar, 
    href: '/emr/scheduling',
    permission: 'appointments:read' 
  },
  { 
    title: 'Patients', 
    icon: Users, 
    href: '/emr/patients',
    permission: 'patients:read' 
  },
  { 
    title: 'Patient Summaries', 
    icon: FileDown, 
    href: '/emr/patient-summaries',
    permission: 'patients:read',
    roles: ['admin', 'physician']
  },
  { 
    title: 'Forms Library', 
    icon: ClipboardList, 
    href: '/emr/forms',
    permission: 'forms:read' 
  },
  { 
    title: 'Submissions', 
    icon: FileText, 
    href: '/emr/submissions',
    permission: 'submissions:read' 
  },
  { 
    title: 'Invoices', 
    icon: Receipt, 
    href: '/emr/invoices',
    permission: 'invoices:read' 
  },
  { 
    title: 'Event Quotes', 
    icon: Sparkles, 
    href: '/emr/event-quotes',
    permission: null,
    adminOnly: true
  },
  { 
    title: 'User Management', 
    icon: Shield, 
    href: '/emr/users',
    permission: 'users:read' 
  },
  { 
    title: 'Settings', 
    icon: Settings, 
    href: '/emr/settings',
    permission: null 
  },
];

export const Sidebar = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const { user, logout, hasPermission } = useAuth();

  const filteredNavItems = navItems.filter(item => {
    if (item.adminOnly && user?.role !== 'admin') return false;
    if (item.roles && !item.roles.includes(user?.role)) return false;
    if (!item.permission) return true;
    return hasPermission(item.permission);
  });

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'flex flex-col h-screen bg-card border-r border-border transition-all duration-300',
          collapsed ? 'w-[72px]' : 'w-[260px]'
        )}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className={cn(
          'flex items-center h-16 px-4 border-b border-border',
          collapsed ? 'justify-center' : 'gap-3'
        )}>
          <img 
            src={LOGO_URL} 
            alt="Apex Medical Group" 
            className="w-10 h-10 rounded-lg object-contain"
          />
          {!collapsed && (
            <div className="animate-fadeIn">
              <h1 className="font-bold text-lg tracking-tight">Apex Medical</h1>
              <p className="text-xs text-muted-foreground">EMR System</p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-2 space-y-1">
            {filteredNavItems.map((item) => {
              const isActive = location.pathname === item.href || 
                             location.pathname.startsWith(item.href + '/');
              
              const NavLink = (
                <Link
                  key={item.href}
                  to={item.href}
                  data-testid={`nav-${item.title.toLowerCase().replace(' ', '-')}`}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200',
                    'hover:bg-muted',
                    isActive && 'bg-primary text-primary-foreground hover:bg-primary/90',
                    collapsed && 'justify-center px-0'
                  )}
                >
                  <item.icon className={cn('w-5 h-5 flex-shrink-0', isActive && 'text-inherit')} />
                  {!collapsed && (
                    <span className="font-medium text-sm">{item.title}</span>
                  )}
                </Link>
              );

              if (collapsed) {
                return (
                  <Tooltip key={item.href}>
                    <TooltipTrigger asChild>
                      {NavLink}
                    </TooltipTrigger>
                    <TooltipContent side="right" className="font-medium">
                      {item.title}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return NavLink;
            })}
          </nav>
        </ScrollArea>

        {/* User Section */}
        <div className="border-t border-border p-4">
          {!collapsed && user && (
            <div className="flex items-center gap-3 mb-3 animate-fadeIn">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <span className="font-semibold text-sm">
                  {user.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user.full_name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user.role}</p>
              </div>
            </div>
          )}
          
          <div className={cn('flex gap-2', collapsed && 'flex-col')}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onToggle}
                  className="h-9 w-9"
                  data-testid="toggle-sidebar"
                >
                  {collapsed ? (
                    <PanelLeft className="h-4 w-4" />
                  ) : (
                    <PanelLeftClose className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side={collapsed ? 'right' : 'top'}>
                {collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={logout}
                  className="h-9 w-9 text-destructive hover:text-destructive hover:bg-destructive/10"
                  data-testid="logout-btn"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side={collapsed ? 'right' : 'top'}>
                Logout
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </aside>
    </TooltipProvider>
  );
};
